import { useCallback, useEffect, useRef } from 'react'
import { gsap, prefersReducedMotion } from 'lib/motion'

const isCoarsePointer = () =>
  typeof window !== 'undefined' && window.matchMedia('(pointer: coarse)').matches

/**
 * Cursor-following pull for primary CTAs. `strength` is the max travel in px.
 * No-op on touch devices and under prefers-reduced-motion.
 */
export function useMagnetic<T extends HTMLElement = HTMLElement>(strength = 6) {
  const ref = useRef<T>(null)

  useEffect(() => {
    const el = ref.current
    if (!el || prefersReducedMotion() || isCoarsePointer()) return undefined

    const xTo = gsap.quickTo(el, 'x', { duration: 0.4, ease: 'power3.out' })
    const yTo = gsap.quickTo(el, 'y', { duration: 0.4, ease: 'power3.out' })

    const onMove = (e: PointerEvent) => {
      const r = el.getBoundingClientRect()
      xTo(((e.clientX - (r.left + r.width / 2)) / r.width) * strength * 2)
      yTo(((e.clientY - (r.top + r.height / 2)) / r.height) * strength * 2)
    }
    const onLeave = () => {
      xTo(0)
      yTo(0)
    }

    el.addEventListener('pointermove', onMove)
    el.addEventListener('pointerleave', onLeave)
    return () => {
      el.removeEventListener('pointermove', onMove)
      el.removeEventListener('pointerleave', onLeave)
      gsap.killTweensOf(el)
    }
  }, [strength])

  return ref
}

/**
 * Spins an element by 180deg per call — used for the swap flip control so the
 * motion states the cause (you flipped the pair) rather than decorating it.
 */
export function useSpinOnAction<T extends HTMLElement = HTMLElement>() {
  const ref = useRef<T>(null)
  const turns = useRef(0)

  const spin = useCallback(() => {
    const el = ref.current
    if (!el || prefersReducedMotion()) return
    turns.current += 1
    gsap.to(el, {
      rotate: turns.current * 180,
      duration: 0.45,
      ease: 'back.out(1.7)',
      overwrite: true,
    })
  }, [])

  return [ref, spin] as const
}
